import { useQueries } from "@tanstack/react-query"
import { useLocation } from "react-router-dom"
import { buildQueries } from "./breadcrumb/buildQueries"
import { isLikelyGuid, knownSegments } from "./breadcrumb/resolverMap"
import { BreadcrumbUnit } from "./breadcrumb/types"

export interface Page {
  label: string
  url: string
}

const PLACEHOLDER = "..."
const DEFAULT_TAB_HASH = "#details"

const firstUpper = (w: string) =>
  w.length === 0 ? "" : w.charAt(0).toUpperCase() + w.slice(1)

function parseSegments(pathname: string): BreadcrumbUnit[] {
  const raw = pathname.split("/").filter(Boolean)
  const units: BreadcrumbUnit[] = []
  let i = 0
  while (i < raw.length) {
    const name = raw[i]
    const next: string | undefined = raw[i + 1]
    const nextIsKnown = next !== undefined && knownSegments.has(next)
    const id = next !== undefined && !nextIsKnown ? next : undefined
    units.push({ name, id })
    i += id === undefined ? 1 : 2
  }
  return units
}

function buildStaticUrl(
  unit: BreadcrumbUnit,
  parentIsEntity: boolean,
  lastEntityUrl: string,
  segmentPath: string
): string {
  if (parentIsEntity && lastEntityUrl) {
    return `${lastEntityUrl}#${unit.name}`
  }
  return segmentPath
}

function resolveEntityLabel(
  unit: BreadcrumbUnit,
  index: number,
  resolvedByUnit: Map<number, string>
): string {
  if (unit.id && isLikelyGuid(unit.id)) {
    return resolvedByUnit.get(index) ?? firstUpper(unit.id)
  }
  return firstUpper(unit.id ?? "")
}

type PagesBuildResult = {
  pages: Page[]
  lastEntityUrl: string
  pathSoFar: string
}

function buildPages(
  units: BreadcrumbUnit[],
  resolvedByUnit: Map<number, string>
): PagesBuildResult {
  const pages: Page[] = [{ label: "Home", url: "/" }]
  let pathSoFar = ""
  let lastEntityUrl = ""

  for (let k = 0; k < units.length; k++) {
    const unit = units[k]
    const prevUnit = k > 0 ? units[k - 1] : undefined
    const parentIsEntity = prevUnit?.id !== undefined
    const segmentPath = `${pathSoFar}/${unit.name}`
    const entityPath = unit.id ? `${segmentPath}/${unit.id}` : segmentPath

    const staticUrl = buildStaticUrl(unit, parentIsEntity, lastEntityUrl, segmentPath)
    pages.push({ label: firstUpper(unit.name), url: staticUrl })

    if (unit.id) {
      pages.push({
        label: resolveEntityLabel(unit, k, resolvedByUnit),
        url: entityPath,
      })
      lastEntityUrl = entityPath
    }

    pathSoFar = entityPath
  }

  return { pages, lastEntityUrl, pathSoFar }
}

export function useVisitedPages(): Page[] {
  const { pathname, hash } = useLocation()
  const units = parseSegments(pathname)
  const breadcrumbQueries = buildQueries(units)

  const results = useQueries({
    queries: breadcrumbQueries.map((q) => ({
      queryKey: q.queryKey,
      queryFn: q.queryFn,
      retry: 1,
      staleTime: 30_000,
    })),
  })

  const resolvedByUnit = new Map<number, string>()
  breadcrumbQueries.forEach((q, idx) => {
    const r = results[idx]
    if (r.isPending || r.isError) {
      resolvedByUnit.set(q.unitIndex, PLACEHOLDER)
    } else {
      resolvedByUnit.set(q.unitIndex, q.select(r.data) || PLACEHOLDER)
    }
  })

  const { pages, lastEntityUrl, pathSoFar } = buildPages(units, resolvedByUnit)

  if (hash && hash !== DEFAULT_TAB_HASH && lastEntityUrl) {
    const tabName = hash.startsWith("#") ? hash.slice(1) : hash
    pages.push({ label: firstUpper(tabName), url: `${pathSoFar}${hash}` })
  }

  return pages
}
